import { useEffect, useMemo, useState } from "react";
import type { EstimatorResult } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, parseMoney } from "../lib/format";
import Select from "../components/Select";

type ItemType = "monthly" | "plan" | "oneoff";
interface NewItem {
  key: string;
  label: string;
  type: ItemType;
  amountMinor: number;
  numPayments?: number;
  depositMinor?: number | null;
  finalMinor?: number | null;
}
type ReplaceMode = "stop" | "end";
/** An existing expense that stops in a scenario — from its own chosen month. */
interface Removal { from: string; mode: ReplaceMode; }

const TYPE_OPTS = [
  { value: "monthly", label: "Ongoing monthly" },
  { value: "plan", label: "Fixed-term plan" },
  { value: "oneoff", label: "One-off lump sum" },
];

interface Row {
  period: string; label: string; incomeMinor: number;
  out: number; baseOut: number; left: number; baseLeft: number; isEffect: boolean;
}

// "What-if" planner — name a purchase, pick what it replaces, and get a verdict
// + a chart of your money month-by-month, all off your real bills/plans/salary.
export default function Estimator() {
  const [data, setData] = useState<EstimatorResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [effectPeriod, setEffectPeriod] = useState("");
  const [items, setItems] = useState<NewItem[]>([]);
  const [removed, setRemoved] = useState<Record<string, Removal>>({});
  const [showMonths, setShowMonths] = useState(false);

  useEffect(() => {
    api.estimator().then((d) => { setData(d); setEffectPeriod(d.fromPeriod); }).catch((e) => setError((e as Error).message));
  }, []);

  const rows: Row[] = useMemo(() => {
    if (!data) return [];
    const effectIdx = Math.max(0, data.periods.findIndex((p) => p.period === effectPeriod));
    const planAmt = (it: NewItem, seq: number) =>
      seq === 0 && it.depositMinor ? it.depositMinor
      : seq === (it.numPayments ?? 1) - 1 && it.finalMinor ? it.finalMinor
      : it.amountMinor;
    return data.periods.map((p, i) => {
      let out = 0, baseOut = 0;
      for (const l of p.lines) {
        baseOut += l.amountMinor;
        const rem = removed[`${l.kind}:${l.refId}`];
        if (rem && rem.mode === "stop" && p.period >= rem.from) continue; // gone from its own date
        out += l.amountMinor;
      }
      if (i >= effectIdx) {
        for (const it of items) {
          if (it.type === "monthly") out += it.amountMinor;
          else if (it.type === "oneoff") { if (i === effectIdx) out += it.amountMinor; }
          else { const seq = i - effectIdx; if (seq < (it.numPayments ?? 0)) out += planAmt(it, seq); }
        }
      }
      return { period: p.period, label: p.label, incomeMinor: p.incomeMinor, out, baseOut, left: p.incomeMinor - out, baseLeft: p.incomeMinor - baseOut, isEffect: i === effectIdx };
    });
  }, [data, effectPeriod, items, removed]);

  if (error) return <div className="state state--error" role="alert">Couldn’t load the estimator: {error}</div>;
  if (!data) return <div className="state">Loading…</div>;

  const effectIdx = Math.max(0, rows.findIndex((r) => r.isEffect));
  const horizon = rows.slice(effectIdx);
  const hasScenario = items.length > 0 || Object.keys(removed).length > 0;
  const income = rows[effectIdx]?.incomeMinor ?? 0;
  const monthlyDelta = (rows[effectIdx]?.out ?? 0) - (rows[effectIdx]?.baseOut ?? 0);
  const tightest = horizon.reduce((m, r) => (r.left < m.left ? r : m), horizon[0] ?? rows[0]);
  const minLeft = tightest?.left ?? 0;
  const totalExtra = horizon.reduce((t, r) => t + (r.out - r.baseOut), 0);

  const comfort = Math.max(20000, Math.round(income * 0.1)); // £200 or 10% of salary
  const verdict: "good" | "tight" | "short" | "none" =
    !hasScenario ? "none" : minLeft < 0 ? "short" : minLeft < comfort ? "tight" : "good";

  const commitments = [...data.plans, ...data.bills];
  const byKey = new Map(commitments.map((c) => [`${c.kind}:${c.id}`, c]));
  const available = commitments.filter((c) => removed[`${c.kind}:${c.id}`] == null);

  // A plan/bill naturally ending inside the horizon that eases things afterwards.
  let easeAt: Row | null = null;
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].baseLeft - rows[i - 1].baseLeft > Math.max(3000, income * 0.02) && (!easeAt || rows[i].baseLeft - rows[i - 1].baseLeft > (easeAt as any).jump)) {
      easeAt = { ...rows[i] }; (easeAt as any).jump = rows[i].baseLeft - rows[i - 1].baseLeft;
    }
  }

  return (
    <div className="page estimator">
      <div className="page__header">
        <div>
          <p className="page__eyebrow">Estimator</p>
          <h1 className="page__title">What-if planner</h1>
          <p className="page__lede">See what a big purchase does to every month ahead — from your real bills, plans and salary.</p>
        </div>
      </div>

      <div className="est-layout">
        {/* Builder */}
        <aside className="est-build panel">
          <div className="field est-when">
            <span className="field__label">Starting from</span>
            <Select value={effectPeriod} onChange={setEffectPeriod} ariaLabel="Starting from"
              options={data.periods.map((p) => ({ value: p.period, label: p.label }))} />
          </div>

          <div className="est-group">
            <h3 className="est-h">The cost</h3>
            {items.map((it) => (
              <div className={"est-card est-card--" + it.type} key={it.key}>
                <div className="est-card__body">
                  <span className="est-card__name">{it.label || "New cost"}</span>
                  <span className="est-card__meta">{describeItem(it)}</span>
                </div>
                <button className="icon-btn" aria-label="Remove" onClick={() => setItems((xs) => xs.filter((x) => x.key !== it.key))}>✕</button>
              </div>
            ))}
            <AddCost onAdd={(it) => setItems((xs) => [...xs, it])} />
          </div>

          <div className="est-group">
            <h3 className="est-h">Expenses that stop <span className="est-step__opt">optional</span></h3>
            <p className="est-ghint">Trading in, cancelling or clearing something? Add it and pick from when.</p>
            {available.length > 0 && (
              <Select value="" onChange={(v) => v && setRemoved((r) => ({ ...r, [v]: { from: effectPeriod, mode: "stop" } }))}
                placeholder="Add an expense that stops…" ariaLabel="Add an expense that stops"
                options={available.map((c) => ({ value: `${c.kind}:${c.id}`, label: `${c.name} · ${formatMoney(c.amountMinor)}${c.kind === "bill" ? "/mo" : ""}` }))} />
            )}
            {Object.entries(removed).map(([key, rem]) => {
              const c = byKey.get(key);
              if (!c) return null;
              return (
                <div className="est-chip" key={key}>
                  <div className="est-chip__top">
                    <span className="est-chip__name">{c.name}</span>
                    <span className="est-chip__amt">−{formatMoney(c.amountMinor)}{c.kind === "bill" ? "/mo" : ""}</span>
                    <button className="icon-btn est-chip__x" aria-label="Remove" onClick={() => setRemoved((r) => { const n = { ...r }; delete n[key]; return n; })}>✕</button>
                  </div>
                  <div className="est-chip__ctrl">
                    {c.canRunToEnd && (
                      <div className="est-chip__mode">
                        <button className={"seg-mini" + (rem.mode === "stop" ? " is-on" : "")} onClick={() => setRemoved((r) => ({ ...r, [key]: { ...r[key], mode: "stop" } }))}>Stops</button>
                        <button className={"seg-mini" + (rem.mode === "end" ? " is-on" : "")} onClick={() => setRemoved((r) => ({ ...r, [key]: { ...r[key], mode: "end" } }))}>Runs to end</button>
                      </div>
                    )}
                    {rem.mode === "stop" && (
                      <label className="est-chip__from">from
                        <Select value={rem.from} onChange={(v) => setRemoved((r) => ({ ...r, [key]: { ...r[key], from: v } }))}
                          ariaLabel="Stops from" options={data.periods.map((p) => ({ value: p.period, label: p.label }))} />
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Impact */}
        <section className="est-impact">
        {!hasScenario ? (
          <div className="est-empty">Add a cost or a stopped expense to see the impact.</div>
        ) : (
          <>
            <div className={"est-verdict est-verdict--" + verdict}>
              <span className="est-verdict__icon">{verdict === "good" ? "✓" : verdict === "tight" ? "!" : "✕"}</span>
              <div className="est-verdict__body">
                <span className="est-verdict__head">
                  {verdict === "good" ? "Comfortably affordable" : verdict === "tight" ? "Doable, but tight" : "This would stretch you"}
                </span>
                <span className="est-verdict__sub">
                  {monthlyDelta >= 0 ? `+${formatMoney(monthlyDelta)}/mo` : `${formatMoney(monthlyDelta)}/mo`}
                  {" · "}
                  {verdict === "short"
                    ? <>short <strong>{formatMoney(-minLeft)}</strong> in {tightest?.label}</>
                    : <>at least <strong>{formatMoney(minLeft)}</strong> left each month</>}
                </span>
              </div>
            </div>

            <ImpactChart rows={rows} effectIdx={effectIdx} verdict={verdict} tightest={tightest} />

            <ul className="est-insights">
              <li>{monthlyDelta > 0 ? <>Adds <strong>{formatMoney(monthlyDelta)}</strong> to your monthly outgoings.</> : monthlyDelta < 0 ? <>Cuts <strong>{formatMoney(-monthlyDelta)}</strong> off your monthly outgoings.</> : <>No change to your monthly outgoings.</>}</li>
              <li>Tightest month is <strong>{tightest?.label}</strong>{minLeft < 0 ? <> — <span className="est-neg">{formatMoney(-minLeft)} short</span>.</> : <> with <strong>{formatMoney(minLeft)}</strong> left.</>}</li>
              {totalExtra !== 0 && <li>Over the next {horizon.length} months it {totalExtra > 0 ? "costs" : "saves"} <strong>{formatMoney(Math.abs(totalExtra))}</strong> versus now.</li>}
              {easeAt && <li>Things ease from <strong>{easeAt.label}</strong> — about <strong>{formatMoney((easeAt as any).jump)}</strong> more spare than the month before (a cost drops off).</li>}
            </ul>

            <button className="est-detailtoggle" onClick={() => setShowMonths((s) => !s)}>{showMonths ? "Hide month-by-month" : "Show month-by-month"}</button>
            {showMonths && (
              <div className="est-months">
                {rows.map((r) => (
                  <div className={"est-mrow" + (r.isEffect ? " is-effect" : "")} key={r.period}>
                    <span className="est-mrow__mon">{r.label}{r.isEffect && <span className="est-badge">starts</span>}</span>
                    <span className="est-mrow__detail">salary {formatMoney(r.incomeMinor)} − bills {formatMoney(r.out)}</span>
                    <span className={"est-mrow__val" + (r.left < 0 ? " est-neg" : "")}>{formatMoney(r.left)} left</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <p className="est-note">Assumes each month’s salary lands and every bill/plan is paid. Day-to-day spending isn’t included.</p>
        </section>
      </div>
    </div>
  );
}

// ---- Chart: remaining per month, baseline (faint) vs with the purchase (bold) ----
function ImpactChart({ rows, effectIdx, verdict, tightest }: { rows: Row[]; effectIdx: number; verdict: string; tightest: Row | undefined }) {
  const W = 680, H = 230, padL = 10, padR = 10, padT = 18, padB = 30;
  const n = rows.length;
  const stroke = verdict === "short" ? "var(--color-status-red)" : verdict === "tight" ? "var(--color-status-amber)" : "var(--color-status-green)";
  const vals = rows.flatMap((r) => [r.left, r.baseLeft]);
  const maxV = Math.max(...vals, 1);
  const minV = Math.min(...vals, 0);
  const x = (i: number) => padL + (n <= 1 ? 0 : (i * (W - padL - padR)) / (n - 1));
  const y = (v: number) => padT + (1 - (v - minV) / (maxV - minV || 1)) * (H - padT - padB);
  const line = (key: "left" | "baseLeft") => rows.map((r, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(r[key]).toFixed(1)}`).join(" ");
  const area = `${line("left")} L ${x(n - 1).toFixed(1)} ${y(minV).toFixed(1)} L ${x(0).toFixed(1)} ${y(minV).toFixed(1)} Z`;
  const tightIdx = tightest ? rows.findIndex((r) => r.period === tightest.period) : -1;

  return (
    <svg className="est-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Money left each month">
      <defs>
        <linearGradient id="estFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {minV < 0 && <line x1={padL} y1={y(0)} x2={W - padR} y2={y(0)} className="est-svg__zero" />}
      {/* effect-month marker */}
      <line x1={x(effectIdx)} y1={padT - 6} x2={x(effectIdx)} y2={H - padB} className="est-svg__mark" />
      <path d={area} fill="url(#estFill)" />
      <path d={line("baseLeft")} className="est-svg__base" fill="none" />
      <path d={line("left")} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {rows.map((r, i) => (
        <g key={r.period}>
          <circle cx={x(i)} cy={y(r.left)} r={i === tightIdx ? 4.5 : 3} fill={i === tightIdx ? stroke : "var(--color-white)"} stroke={stroke} strokeWidth="1.6">
            <title>{`${r.label}: ${formatMoney(r.left)} left`}</title>
          </circle>
          {(i === 0 || i === n - 1 || i % 2 === 1) && (
            <text x={x(i)} y={H - 10} textAnchor="middle" className="est-svg__xlabel">{r.label.split(" ")[0]}</text>
          )}
        </g>
      ))}
    </svg>
  );
}

function describeItem(it: NewItem): string {
  if (it.type === "monthly") return `${formatMoney(it.amountMinor)}/mo · ongoing`;
  if (it.type === "oneoff") return `${formatMoney(it.amountMinor)} · one-off`;
  const parts = [`${formatMoney(it.amountMinor)}/mo × ${it.numPayments ?? 0}`];
  if (it.depositMinor) parts.push(`deposit ${formatMoney(it.depositMinor)}`);
  if (it.finalMinor) parts.push(`final ${formatMoney(it.finalMinor)}`);
  return parts.join(" · ");
}

function AddCost({ onAdd }: { onAdd: (it: NewItem) => void }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ItemType>("monthly");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [payments, setPayments] = useState("");
  const [deposit, setDeposit] = useState("");
  const [final, setFinal] = useState("");
  const [err, setErr] = useState<string | null>(null);

  function add() {
    const minor = parseMoney(amount);
    if (minor == null || minor <= 0) return setErr(type === "oneoff" ? "Enter the amount" : "Enter the monthly amount");
    const it: NewItem = { key: crypto.randomUUID(), label: label.trim(), type, amountMinor: minor };
    if (type === "plan") {
      const nn = Number(payments);
      if (!Number.isInteger(nn) || nn < 1) return setErr("Enter the number of payments");
      it.numPayments = nn; it.depositMinor = parseMoney(deposit) || null; it.finalMinor = parseMoney(final) || null;
    }
    onAdd(it);
    setOpen(false); setLabel(""); setAmount(""); setPayments(""); setDeposit(""); setFinal(""); setErr(null); setType("monthly");
  }

  if (!open) return <button className="btn btn--ghost est-add" onClick={() => setOpen(true)}>+ Add a cost</button>;
  return (
    <div className="est-form">
      <div className="form__row">
        <label className="field"><span className="field__label">What is it?</span>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="New car finance" autoFocus /></label>
        <div className="field"><span className="field__label">Type</span>
          <Select value={type} onChange={(v) => setType(v as ItemType)} ariaLabel="Type" options={TYPE_OPTS} /></div>
      </div>
      <div className="form__row">
        <label className="field"><span className="field__label">{type === "oneoff" ? "Amount (£)" : "Amount per month (£)"}</span>
          <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="350.00" /></label>
        {type === "plan" && (
          <label className="field"><span className="field__label">Number of payments</span>
            <input className="input" inputMode="numeric" value={payments} onChange={(e) => setPayments(e.target.value)} placeholder="36" /></label>
        )}
      </div>
      {type === "plan" && (
        <div className="form__row">
          <label className="field"><span className="field__label">Deposit (£, optional)</span>
            <input className="input" inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="2000.00" /></label>
          <label className="field"><span className="field__label">Final payment (£, optional)</span>
            <input className="input" inputMode="decimal" value={final} onChange={(e) => setFinal(e.target.value)} placeholder="5000.00" /></label>
        </div>
      )}
      {err && <p className="form__error">{err}</p>}
      <div className="form__actions">
        <button className="btn btn--ghost" onClick={() => { setOpen(false); setErr(null); }}>Cancel</button>
        <button className="btn btn--primary" onClick={add}>Add cost</button>
      </div>
    </div>
  );
}
